import { Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { StandardPanel } from "../../../_components/panel/standard.panel";
import { AuctionAnnualStateResponse, clampAnnualTimerSeconds, computeAnnualTimerRemainingSeconds } from "./auction-annual-shared";

export type AnnualTimerAnchors = Pick<AuctionAnnualStateResponse, "timerDurationSeconds" | "timerEndsAtUtc" | "timerRemainingSeconds">;

function formatAnnualTimerDisplay(sec: number) {
    const safe = clampAnnualTimerSeconds(sec);
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Server-synced read-only countdown — shared by admin, monitor, customer, and proxy. */
export function AuctionAnnualCountdown(params: { timerAnchors: AnnualTimerAnchors | null | undefined }) {
    const [secondsLeft, setSecondsLeft] = useState(() => computeAnnualTimerRemainingSeconds(params.timerAnchors));

    useEffect(() => {
        let intervalId: number | null = null;

        const tick = () => {
            const next = computeAnnualTimerRemainingSeconds(params.timerAnchors);
            setSecondsLeft(next);
            if (next <= 0 && intervalId !== null) {
                window.clearInterval(intervalId);
                intervalId = null;
            }
            return next;
        };

        tick();
        intervalId = window.setInterval(tick, 1000);

        return () => {
            if (intervalId !== null) window.clearInterval(intervalId);
        };
    }, [params.timerAnchors?.timerDurationSeconds, params.timerAnchors?.timerEndsAtUtc, params.timerAnchors?.timerRemainingSeconds]);

    return (
        <StandardPanel title="Countdown" loading={false}>
            <Typography
                component="div"
                aria-live="polite"
                aria-atomic="true"
                sx={{
                    textAlign: "center",
                    fontVariantNumeric: "tabular-nums",
                    fontSize: { xs: "3.5rem", sm: "4.25rem" },
                    fontWeight: 700,
                    lineHeight: 1.05,
                    letterSpacing: 1,
                }}
            >
                {formatAnnualTimerDisplay(secondsLeft)}
            </Typography>
        </StandardPanel>
    );
}
